/**
 * One paginated Convex query, walked to its end.
 *
 * Eval reads are paginated because an eval row is big — four Markdown bodies — and a Convex
 * transaction reads at most 16 MiB, so the page size is a transaction bound rather than a product
 * decision. The screens over those reads are whole-list screens: the catalog's search has to reach
 * the last case, and a version chain is a history. So this hook asks for the next page the moment
 * one lands, and reports a single status over the whole walk rather than per page.
 *
 * The comparator runs over everything loaded so far, never inside a page: a page-local sort would
 * order rows within it and lie about the list.
 */
import {
  type PaginatedQueryArgs,
  type PaginatedQueryItem,
  type PaginatedQueryReference,
  type PaginationStatus,
  usePaginatedQuery,
} from "convex/react";
import { useEffect, useMemo } from "react";

/** Where the walk stands: nothing loaded yet, part of the list, or all of it. */
export type ExhaustiveWalkStatus = "loading" | "loading-more" | "complete";

export type ExhaustiveWalk<Item> = {
  rows: Item[];
  status: ExhaustiveWalkStatus;
};

export type ExhaustiveWalkOptions<Item> = {
  /** Rows per request. The server clamps it; asking for more than the ceiling is not an error. */
  pageSize: number;
  /** Applied across every loaded page. Omit it to keep the order the server returned. */
  compare?: (left: Item, right: Item) => number;
};

const NO_ROWS: never[] = [];

/**
 * `complete` is the only status that means "this is the whole list".
 *
 * A page that comes back empty is not the end — the linked-skill scan filters inside the page and
 * hands the cursor back untouched — so nothing may read an empty result as "there is nothing" until
 * the walk says so.
 */
function walkStatus(status: PaginationStatus): ExhaustiveWalkStatus {
  if (status === "Exhausted") return "complete";
  if (status === "LoadingFirstPage") return "loading";
  return "loading-more";
}

export function useExhaustivePaginatedQuery<Query extends PaginatedQueryReference>(
  query: Query,
  args: PaginatedQueryArgs<Query> | "skip",
  options: ExhaustiveWalkOptions<PaginatedQueryItem<Query>>
): ExhaustiveWalk<PaginatedQueryItem<Query>> {
  const { compare, pageSize } = options;
  const { loadMore, results, status } = usePaginatedQuery(query, args, {
    initialNumItems: pageSize,
  });

  useEffect(() => {
    if (status === "CanLoadMore") {
      loadMore(pageSize);
    }
  }, [loadMore, pageSize, status]);

  const rows = useMemo(() => (compare ? [...results].sort(compare) : results), [compare, results]);

  // A skipped query has no walk to report on, and holding "complete" there would tell a caller
  // that an empty list is the answer.
  if (args === "skip") {
    return { rows: NO_ROWS, status: "loading" };
  }
  return { rows, status: walkStatus(status) };
}
